import { useEffect } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DataSetsPanel from 'components/DataSetsPanel/DataSetsPanel';
import DateFieldsWrapper from 'components/DateFieldsWrapper/DateFieldsWrapper';
import NumberField from 'components/NumberField/NumberField';
import OrgPathField from 'components/OrhPathField/OrgPathField';
import Select from 'components/Select/Select';
import TextField from 'components/TextField/TextField';
import { DATA_SETS_ENUM_TYPES } from 'constants/dataSets';
import {
  DATE_CLOSED,
  DATE_OPENED,
  EQUITY_SHAREHOLDING,
  FINANCIAL_CONTROL,
  NUMBER_OF_EMPLOYEES,
  OLD_ORG_PATH,
  ORG_PATH,
  OWNERSHIP,
  PROFIT_CENTRE_NAME,
  PROFIT_CENTRE_UNIQUE_IDENTIFIER,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { NUMBER_FORMATS } from 'constants/interfaces';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IGeneralPanelProps from '../../interfaces/IGeneralPanelProps';

import styles from '../../sass/CreateProfitCentreView.module.scss';
import stylesCurrency from 'components/FieldWithCurrency/scss/FieldWithCurrency.module.scss';

function GeneralPanel({
  control,
  recordId,
  dateOpened,
  onDateOpenedChange,
  enumData,
  orgStructureData,
  logicalName,
  setValue,
  modalRef,
  menuPosition,
  vcOptions,
  handleGetVcOptions,
  vcOptionsList,
  listPosition,
  requiresParentSelection,
}: IGeneralPanelProps) {
  const { t } = useTranslation();
  const isOrgPathRequired = requiresParentSelection ?? true;

  const oldOrgPathValue = useWatch({
    control,
    name: OLD_ORG_PATH,
  });

  const orgPathValue = useWatch({
    control,
    name: ORG_PATH,
  });

  useEffect(() => {
    if (oldOrgPathValue && (!orgPathValue || orgPathValue === '')) {
      setValue(ORG_PATH, oldOrgPathValue);
    }
  }, [oldOrgPathValue, orgPathValue, setValue]);

  return (
    <DataSetsPanel title={t('profitCentres.createProfitCentre.generalPanel')} className={styles.fullWidth}>
      <Controller
        control={control}
        name={ORG_PATH}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <OrgPathField
            name={ORG_PATH}
            value={value || ''}
            label={t('profitCentres.createProfitCentre.fields.structure')}
            error={error?.message}
            onChange={(valuePath) => onChange(valuePath)}
            orgStructureData={orgStructureData || []}
            placeholder={t('profitCentres.createProfitCentre.fields.structure')}
            logicalName={logicalName}
            orgPathValue={oldOrgPathValue}
            requiresParentSelection={requiresParentSelection}
            required={isOrgPathRequired}
          />
        )}
      />
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={PROFIT_CENTRE_NAME}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <TextField
              name={name}
              value={value}
              label={t('profitCentres.createProfitCentre.fields.name')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={t('profitCentres.createProfitCentre.fields.name')}
            />
          )}
        />
        <Controller
          control={control}
          name={PROFIT_CENTRE_UNIQUE_IDENTIFIER}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <TextField
              name={name}
              value={value}
              label={t('profitCentres.createProfitCentre.fields.uniqueIdentifier')}
              error={error?.message}
              onChange={(event) => onChange(event.target.value)}
              required
              placeholder={t('profitCentres.createProfitCentre.fields.uniqueIdentifier')}
              disabled={!!recordId}
            />
          )}
        />
        <Controller
          control={control}
          name={VALUE_CHAIN_LIST}
          render={({
            field: { name, value: valueChain, onChange: onChangeValueChain },
            fieldState: { error: errorCurrency },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainPosition')}
              label={t('common.valueChainPosition')}
              options={vcOptionsList}
              value={valueChain?.value ? valueChain : undefined}
              onChange={(value) => {
                const selectedValue = value as IOption;
                onChangeValueChain(selectedValue);
                handleGetVcOptions(selectedValue.value);
                setValue(VALUE_CHAIN_SECTION, undefined as never);
              }}
              error={errorCurrency?.message}
              className={stylesCurrency.select}
              modalRef={modalRef}
              menuPosition={menuPosition}
              isDisabled
              required
            />
          )}
        />
        <Controller
          control={control}
          name={VALUE_CHAIN_SECTION}
          render={({
            field: { name, value: valueChainSection, onChange: onChangeValueChainSection },
            fieldState: { error: errorCurrencySection },
          }) => (
            <Select
              name={name}
              placeholder={t('common.valueChainCategory')}
              label={t('common.valueChainCategory')}
              options={vcOptions || listPosition}
              value={valueChainSection?.value ? valueChainSection : null}
              onChange={onChangeValueChainSection}
              error={errorCurrencySection?.message}
              className={stylesCurrency.select}
              modalRef={modalRef}
              menuPosition={menuPosition}
              isDisabled={listPosition?.length === 0 && !recordId}
              required
            />
          )}
        />
        <Controller
          control={control}
          name={OWNERSHIP}
          render={({
            field: { name, value: valueChain, onChange: onChangeValueChain },
            fieldState: { error: errorCurrency },
          }) => (
            <Select
              name={name}
              placeholder={t('profitCentres.createProfitCentre.fields.ownership')}
              label={t('profitCentres.createProfitCentre.fields.ownership')}
              options={enumData?.[DATA_SETS_ENUM_TYPES.ownership]}
              value={valueChain?.value ? valueChain : undefined}
              onChange={onChangeValueChain}
              error={errorCurrency?.message}
              className={stylesCurrency.select}
              modalRef={modalRef}
              menuPosition={menuPosition}
              required
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={EQUITY_SHAREHOLDING}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <NumberField
              name={name}
              value={value}
              label={t('profitCentres.createProfitCentre.fields.equityShareholdingCurrency')}
              error={error?.message}
              onChange={(values) => onChange(values.value)}
              type={NUMBER_FORMATS.percent}
              placeholder={t('profitCentres.createProfitCentre.fields.equityShareholdingCurrency')}
              allowNegative={false}
              max={100}
            />
          )}
        />
        <Controller
          control={control}
          name={FINANCIAL_CONTROL}
          render={({ field: { name, value, onChange }, fieldState: { error } }) => (
            <NumberField
              name={name}
              value={value}
              label={t('profitCentres.createProfitCentre.fields.financialControlCurrency')}
              error={error?.message}
              onChange={(values) => onChange(values.value)}
              type={NUMBER_FORMATS.percent}
              placeholder={t('profitCentres.createProfitCentre.fields.financialControlCurrency')}
              allowNegative={false}
              max={100}
            />
          )}
        />
      </div>
      <div className={styles.wrapper}>
        <Controller
          control={control}
          name={NUMBER_OF_EMPLOYEES}
          render={({
            field: {
              name,
              value,
              onChange,
            },
            fieldState: { error },
          }) => (
            <NumberField
              name={name}
              value={value}
              label={t('profitCentres.createProfitCentre.fields.numberOfEmployees')}
              error={error?.message}
              onChange={(values) => onChange(values.value)}
              decimalScale={0}
              placeholder={t('profitCentres.createProfitCentre.fields.numberOfEmployees')}
              allowNegative={false}
            />
          )}
        />
      </div>
      <DateFieldsWrapper
        control={control}
        startFieldName={DATE_OPENED}
        endFieldName={DATE_CLOSED}
        startDate={dateOpened}
        onStartDateChange={onDateOpenedChange}
        startFieldLabel={t('profitCentres.createProfitCentre.fields.dateOpened')}
        endFieldLabel={t('profitCentres.createProfitCentre.fields.dateClosed')}
        startRequired
        className={styles.wrapper}
      />
    </DataSetsPanel>
  );
}

export default GeneralPanel;
